import { createElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import type { CreateSubjectInput, UpdateSubjectInput } from '@frappi/shared';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { useUIStore } from '@/stores/ui-store';

export const subjectKeys = {
  all: ['subjects'] as const,
  lists: () => [...subjectKeys.all, 'list'] as const,
  list: (filters?: { studentId?: string }) => [...subjectKeys.lists(), filters] as const,
  details: () => [...subjectKeys.all, 'detail'] as const,
  detail: (id: string) => [...subjectKeys.details(), id] as const,
};

export function useSubjects(studentId?: string) {
  return useQuery({
    queryKey: subjectKeys.list({ studentId }),
    queryFn: () => api.subjects.list(studentId),
  });
}

export function useSubject(id: string) {
  return useQuery({
    queryKey: subjectKeys.detail(id),
    queryFn: () => api.subjects.get(id),
    enabled: !!id,
  });
}

export function useCreateSubject() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (data: CreateSubjectInput) => api.subjects.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
      toast.success(t('subject.created'));
    },
    onError: (error: Error) => {
      if (error instanceof ApiRequestError && error.isQuotaExceeded()) {
        toast(t('quotaExceeded.title'), {
          description: t('quotaExceeded.description'),
          icon: createElement(Crown, { className: 'h-5 w-5 text-amber-500' }),
          action: {
            label: t('quotaExceeded.upgrade'),
            onClick: () => { useUIStore.getState().setUpgradeDialogOpen(true); },
          },
          duration: 8000,
        });
        return;
      }
      toast.error(error.message || t('subject.errorCreating'));
    },
  });
}

export function useUpdateSubject() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubjectInput }) =>
      api.subjects.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subjectKeys.detail(id) });
      toast.success(t('subject.updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('subject.errorUpdating'));
    },
  });
}

export function useDeleteSubject() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (id: string) => api.subjects.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
      toast.success(t('subject.deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('subject.errorDeleting'));
    },
  });
}
